import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { AssetDetails } from './Details/Asset';
import { InsightDetails } from './Details/InsightDetails';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import { SupportingEvidence } from './Details/SupportingEvidence';
// MLImpactChart temporarily commented out — see the ML Impact Analysis chart
// block below (disabled for anomaly detection insights).
// import { MLImpactChart } from './Details/MLImpactChart';
import MlInsightsHandler from 'handlers/ml-insights.handler';
import dayjs from 'dayjs';
import { ActionButton } from './Details/InsightDetails.styles';
import { ArrowLeftIcon } from '@mui/x-date-pickers';
import { SupportingEvidenceAirFilter } from './Details/SupportingEvidenceAirFilter';
import { SupportingEvidenceTpms } from './Details/SupportingEvidenceTpms';
import {
  type AirFilterEvidenceResponse,
  type SupportingEvidenceResponse,
  type TpmsEvidenceResponse,
  type TableInsightItem,
  type InsightsTableNavigationState,
  InsightType,
  INSIGHT_TYPE_KEYS,
} from 'types/ml-insights.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const MlInsightDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as
    | {
        data: TableInsightItem;
        tableState?: InsightsTableNavigationState;
      }
    | undefined;
  const data = locationState?.data;
  const storedTableState = locationState?.tableState;
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const { assets, isLoadingAssetOverview } = useAppSelector(
    (state) => state.assetReducer
  );
  const hierarchyInfo = useAppSelector((state) => state.hierarchyReducer);
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  const enableDarkTheme: boolean = isDarkTheme(theme);
  const [evidence, setEvidence] = useState<
    | SupportingEvidenceResponse
    | AirFilterEvidenceResponse
    | TpmsEvidenceResponse
    | null
    | []
  >(null);
  const [evidencePerTag, setEvidencePerTag] = useState<Array<{
    tag: string;
    evidence: SupportingEvidenceResponse;
  }> | null>(null);

  const mlInsightsHandler = new MlInsightsHandler();
  const { isEvidenceLoading } = useAppSelector(
    (state) => state.mlInsightsReducer
  );
  const notifications = useAppSelector(
    (state) => state.ruleNotificationsReducer.notifications
  );
  const rules = useAppSelector((state) => state.ruleReducer.rules);
  // tagImpact reader is only consumed by the ML Impact Analysis chart, which is
  // temporarily commented out below — keep the setter so the fetch still runs.
  const [, setTagImpact] = useState<
    Array<{
      insight: string;
      days: number;
      avg_engine_hrs_anomaly: number;
      avg_engine_hrs_baseline: number;
      avg_engine_hrs_diff: number;
      avg_engine_hrs_diff_pct: number;
      exp_fuel_rate_diff: number;
    }>
  >([]);
  const [isLoadingImpactAndEvidence, setIsLoadingImpactAndEvidence] =
    useState(true);
  const insightValue = data?.insight ?? data?.issue ?? data?.task ?? '';
  const alertStartTs = data?.alertStartTs ?? data?.timestamp ?? '';
  const alertEndTs = data?.alertEndTs ?? '';

  useEffect(() => {
    if (!data) {
      navigate('/ml-insights', { replace: true });
    }
  }, [data, navigate]);

  useEffect(() => {
    const insight = insightValue;
    const deviceId = data?.device;
    if (!insight) {
      setIsLoadingImpactAndEvidence(false);
      return;
    }
    if (!deviceId) {
      setIsLoadingImpactAndEvidence(false);
      return;
    }
    if (!alertStartTs) {
      setIsLoadingImpactAndEvidence(false);
      return;
    }
    setIsLoadingImpactAndEvidence(true);
    const isAirFilterInsight =
      insight.includes(InsightType.AIR_FILTER_CLOGGED_DISPLAY) ||
      insight === InsightType.AIR_FILTER_CLOGGED;
    const isTransmissionInsight =
      insight === InsightType.TRANSMISSION_INEFFICIENCY;
    const setLoaded = () => {
      setIsLoadingImpactAndEvidence(false);
    };

    /** ISO 8601 UTC timestamp(s) for evidence-values-v2 */
    const evidenceTs = dayjs.utc(alertStartTs).toISOString();
    const evidenceFromTs = evidenceTs;
    const evidenceToTs = alertEndTs
      ? dayjs.utc(alertEndTs).toISOString()
      : undefined;
    const tagNamesForV2 = Array.from(
      new Set(
        (data?.tagNames ?? [])
          .filter((t: unknown): t is string => typeof t === 'string')
          .map((t: string) => t.trim())
          .filter((t: string) => t.length > 0)
      )
    );

    if (isAirFilterInsight) {
      setEvidencePerTag(null);
      Promise.all([
        mlInsightsHandler.getMLImpact(InsightType.AIR_FILTER_CLOGGED),
        mlInsightsHandler.getSupportingEvidence(
          evidenceFromTs,
          deviceId,
          insight,
          false,
          true
        ),
      ])
        .then(([impactRes, evidence]) => {
          setTagImpact(
            Array.isArray(impactRes?.impact) ? impactRes.impact : []
          );
          setEvidence(evidence);
        })
        .catch((error) => {
          console.error('Error fetching ML data:', error);
          setTagImpact([]);
          setEvidence([]);
        })
        .finally(setLoaded);
    } else if (tagNamesForV2.length > 0) {
      setEvidence(null);
      Promise.all([
        mlInsightsHandler.getMLImpact(insight),
        mlInsightsHandler.getSupportingEvidenceV2ForTags(
          tagNamesForV2,
          deviceId,
          evidenceFromTs,
          evidenceToTs
        ),
      ])
        .then(([impactRes, perTag]) => {
          setTagImpact(
            Array.isArray(impactRes?.impact) ? impactRes.impact : []
          );
          setEvidencePerTag(perTag);
        })
        .catch((error) => {
          console.error('Error fetching ML data:', error);
          setTagImpact([]);
          setEvidencePerTag([]);
        })
        .finally(setLoaded);
    } else {
      setEvidencePerTag(null);
      Promise.all([
        mlInsightsHandler.getMLImpact(insight),
        mlInsightsHandler.getSupportingEvidence(
          evidenceFromTs,
          deviceId,
          insight,
          isTransmissionInsight
        ),
      ])
        .then(([impactRes, evidence]) => {
          setTagImpact(
            Array.isArray(impactRes?.impact) ? impactRes.impact : []
          );
          setEvidence(evidence);
        })
        .catch((error) => {
          console.error('Error fetching ML data:', error);
          setTagImpact([]);
          setEvidence([]);
        })
        .finally(setLoaded);
    }
  }, [insightValue, alertStartTs, alertEndTs, data?.device, data?.tagNames]);

  if (!data) {
    return null;
  }

  // Merge live data so device.deviceStatus/daysBehind are recomputed from
  // latestTagData — otherwise the ignition dot uses a stale raw asset.
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);
  const selectedAsset = assetsWithLiveData.find(
    (a) => a.device.deviceId === data.device
  );

  const isAirFilterInsight =
    insightValue.includes(InsightType.AIR_FILTER_CLOGGED_DISPLAY) ||
    insightValue === InsightType.AIR_FILTER_CLOGGED;
  const isTpmsInsight = insightValue
    .toLowerCase()
    .includes(INSIGHT_TYPE_KEYS.TPMS.toLowerCase());

  const usePerTagEvidenceCharts =
    !isAirFilterInsight &&
    !isTpmsInsight &&
    (data.tagNames?.filter((t) => typeof t === 'string' && t.trim().length > 0)
      .length ?? 0) > 0;
  const isRuleNotificationSource =
    String(data.source ?? '').toLowerCase() === 'notification';
  const matchedNotification = isRuleNotificationSource
    ? notifications.find((item) => item.notificationId === data.notificationId)
    : undefined;
  const matchedRule = isRuleNotificationSource
    ? rules.find((item) => item.ruleId === matchedNotification?.ruleId)
    : undefined;
  const notificationConditionsFromInsight =
    isRuleNotificationSource && Array.isArray(data.ruleConditions)
      ? data.ruleConditions
      : undefined;
  const notificationConditionsFromRule = matchedRule?.conditions;
  const notificationConditions =
    notificationConditionsFromInsight ?? notificationConditionsFromRule;

  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <Box
      sx={{
        color: palette.textPrimary,
        borderRadius: '5px',
        marginTop: '3px',
        padding: '10px',
      }}
    >
      {!isLoadingAssetOverview ? (
        <>
          <Box>
            <ActionButton
              startIcon={<ArrowLeftIcon />}
              onClick={() => {
                if (storedTableState) {
                  navigate('/ml-insights', {
                    state: { tableState: storedTableState },
                  });
                } else {
                  navigate('/ml-insights');
                }
              }}
              sx={{
                marginBottom: '4px',
              }}
            >
              {t('back_to_insights')}
            </ActionButton>
            <Grid container spacing={2}>
              {selectedAsset ? (
                <AssetDetails
                  enableDarkTheme={enableDarkTheme}
                  asset={selectedAsset}
                  hierarchyInfo={hierarchyInfo}
                  latestTagData={latestTagData}
                />
              ) : null}
              <InsightDetails
                enableDarkTheme={enableDarkTheme}
                dataVal={data as unknown as Record<string, string>}
              />
            </Grid>
          </Box>
          <Box sx={{ marginTop: '20px' }}>
            <Grid container spacing={2}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                {isAirFilterInsight ? (
                  <SupportingEvidenceAirFilter
                    data={data}
                    evidence={evidence as AirFilterEvidenceResponse}
                    enableDarkTheme={enableDarkTheme}
                  />
                ) : isTpmsInsight ? (
                  <SupportingEvidenceTpms
                    data={data}
                    evidence={evidence as TpmsEvidenceResponse}
                    enableDarkTheme={enableDarkTheme}
                  />
                ) : usePerTagEvidenceCharts ? (
                  isLoadingImpactAndEvidence || isEvidenceLoading ? (
                    <Box sx={{ minHeight: 420, py: 4 }}>
                      <SingleFadeLoader isFullScreen={false} />
                    </Box>
                  ) : evidencePerTag && evidencePerTag.length > 0 ? (
                    <Grid
                      container
                      spacing={2}
                      sx={{ alignItems: 'stretch', flexWrap: 'wrap' }}
                    >
                      {evidencePerTag.map(({ tag, evidence: tagEvidence }) => {
                        const multiChart = evidencePerTag.length > 1;
                        return (
                          <Grid
                            item
                            key={tag}
                            xs={12}
                            sm={12}
                            md={multiChart ? 6 : 12}
                            lg={multiChart ? 6 : 12}
                            sx={{ display: 'flex', minWidth: 0 }}
                          >
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                width: '100%',
                              }}
                            >
                              <SupportingEvidence
                                data={data}
                                evidence={tagEvidence}
                                enableDarkTheme={enableDarkTheme}
                                chartTagName={tag}
                                notificationConditions={notificationConditions}
                                suppressInternalLoader
                              />
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Box sx={{ py: 3, px: 2 }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: palette.textPrimary,
                        }}
                      >
                        {t('no_supporting_evidence_available_for_these_tags')}
                      </Typography>
                    </Box>
                  )
                ) : (
                  <SupportingEvidence
                    data={data}
                    evidence={evidence as SupportingEvidenceResponse}
                    enableDarkTheme={enableDarkTheme}
                    notificationConditions={notificationConditions}
                  />
                )}
              </Grid>
              {/* ML Impact Analysis chart temporarily commented out for anomaly
                  detection insights (was only shown when !isRuleNotificationSource).
              {!isRuleNotificationSource && (
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <MLImpactChart
                    data={data}
                    enableDarkTheme={enableDarkTheme}
                    tagImpact={tagImpact}
                    isLoading={isLoadingImpactAndEvidence}
                  />
                </Grid>
              )}
              */}
            </Grid>
          </Box>
        </>
      ) : (
        <SingleFadeLoader />
      )}
    </Box>
  );
};

export default MlInsightDetails;
